import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Shield, Users, BookOpen, GraduationCap, HandHeart,
  MessageCircle, Heart, Sparkles, Building2,
} from "lucide-react";
import SectionHeader from "@/components/site/SectionHeader";
import InstagramFeed from "@/components/site/InstagramFeed";

const AUDIENCES = [
  {
    icon: Building2,
    title: "Organisations & Professionals",
    text: "Interactive sexual health and wellbeing education workshops for organisations, community services and professionals who support young people.",
    href: "/workshops",
    cta: "Workshops for organisations",
  },
  {
    icon: GraduationCap,
    title: "Schools & Educators",
    text: "Professional education and workshops for teachers, school staff, wellbeing teams, pastoral care staff and other professionals supporting students.",
    href: "/workshops",
    cta: "Workshops for schools",
  },
  {
    icon: Users,
    title: "Rangatahi Education",
    text: "Evidence-informed, age-appropriate and culturally responsive sexual health and wellbeing education that helps rangatahi build knowledge, confidence and skills.",
    href: "/programmes",
    cta: "Our rangatahi programme",
  },
];

const PILLARS = [
  { icon: Sparkles, title: "Community-focused", text: "Programmes shaped by the rangatahi, educators and communities we serve." },
  { icon: Shield, title: "Evidence-informed", text: "Grounded in current research and public-health best practice." },
  { icon: Heart, title: "Culturally responsive", text: "Honouring Te Tiriti, Māori and Pasifika worldviews, and the diversity of rangatahi." },
];

const TOPICS = [
  { icon: HandHeart, title: "Consent & healthy relationships" },
  { icon: BookOpen, title: "Sexual health literacy" },
  { icon: MessageCircle, title: "Sexual health conversations & referral pathways" },
  { icon: Sparkles, title: "Inclusive sexual health education" },
];

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="container-x pt-16 pb-20 sm:pt-24 sm:pb-28 lg:pt-28 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 animate-fade-up">
              <span className="label-eyebrow">— Aoturoa Academy Charitable Trust · CC58012</span>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[62px] font-heading font-semibold tracking-tighter leading-[1.05] text-balance">
                Sexual health education that builds{" "}
                <span className="italic text-crimson">knowledge, confidence</span> &amp; wellbeing.
              </h1>
              <p className="mt-7 text-lg text-ink/65 max-w-xl leading-relaxed">
                Supporting rangatahi, educators and organisations through practical, evidence-informed sexual health
                and wellbeing education across Aotearoa.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <Link to="/workshops" data-testid="hero-workshops-btn" className="btn-crimson">
                  Explore Our Workshops <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/contact" data-testid="hero-enquire-btn" className="btn-primary">
                  Enquire About a Workshop
                </Link>
                <Link to="/about" data-testid="hero-learn-btn" className="btn-outline">
                  Learn More
                </Link>
              </div>
              <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-ink/55">
                <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-crimson animate-pulse" />Hauora-aligned</span>
                <span className="hidden sm:inline">·</span>
                <span>Inclusive &amp; LGBTQIA+ affirming</span>
                <span className="hidden sm:inline">·</span>
                <span>Te Tiriti grounded</span>
              </div>
            </div>

            <div className="lg:col-span-5 relative animate-fade-in">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/3777543/pexels-photo-3777543.jpeg"
                  alt="Diverse rangatahi community"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-bone border border-stone rounded-2xl p-5 max-w-[260px] shadow-[0_12px_50px_rgba(0,0,0,0.08)]">
                <div className="flex items-center gap-2 mb-2">
                  <Heart className="w-4 h-4 text-crimson fill-crimson" />
                  <span className="label-eyebrow !text-ink/60">Our Kaupapa</span>
                </div>
                <p className="text-sm text-ink/75 leading-relaxed">Practical, evidence-informed education for rangatahi and the people who support them.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION PILLARS */}
      <section className="bg-ink text-bone relative overflow-hidden" data-testid="mission-strip">
        <div className="absolute inset-0 grain-overlay opacity-[0.04] pointer-events-none" />
        <div className="container-x relative py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
            {PILLARS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="border-l-2 border-crimson/60 pl-6">
                <Icon className="w-7 h-7 text-crimson mb-5" />
                <h3 className="font-heading text-xl font-semibold text-bone">{title}</h3>
                <p className="mt-3 text-bone/65 leading-relaxed text-sm">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THREE AUDIENCES */}
      <section className="section-y" data-testid="three-audiences">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <SectionHeader
              eyebrow="Who we work with"
              title="Three areas of education. One shared kaupapa."
              description="Aoturoa Academy supports rangatahi — and the educators, professionals and organisations who work alongside them."
            />
            <Link to="/workshops" data-testid="audiences-cta" className="btn-outline self-start">
              Explore workshops <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {AUDIENCES.map(({ icon: Icon, title, text, href, cta }) => (
              <Link
                key={title}
                to={href}
                data-testid={`audience-${title.split(" ")[0].toLowerCase()}`}
                className="card-surface group flex flex-col hover:border-crimson/30"
              >
                <div className="w-12 h-12 rounded-xl bg-sand group-hover:bg-crimson transition-colors flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5 text-ink group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-ink leading-snug">{title}</h3>
                <p className="mt-3 text-ink/65 leading-relaxed flex-1">{text}</p>
                <span className="mt-6 pt-5 border-t border-stone text-sm font-medium text-ink inline-flex items-center gap-1 group-hover:text-crimson transition-colors">
                  {cta} <ArrowUpRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>

          <p className="mt-10 text-sm text-ink/55 italic max-w-3xl">
            Workshops can be tailored depending on the needs of the organisation, school or audience. Aoturoa Academy
            provides education, workshops and professional development — we are not a clinical healthcare provider.
          </p>
        </div>
      </section>

      {/* WHY THIS MATTERS */}
      <section className="bg-sand">
        <div className="container-x py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <span className="label-eyebrow">— Why this matters</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-heading font-semibold leading-tight text-balance">
              Every rangatahi deserves honest, inclusive education about their wellbeing.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-lg text-ink/75 leading-relaxed">
            <p>
              Young people in Aotearoa are navigating identity, relationships and health in a world that doesn't always
              speak their language — and the professionals who support them don't always feel confident having the kōrero.
            </p>
            <p>
              Aoturoa Academy delivers practical, culturally responsive education that meets rangatahi where they are,
              and builds the knowledge and confidence of the whānau, educators, and organisations around them.
            </p>
            <Link to="/about" data-testid="why-learn-more" className="inline-flex items-center gap-1 text-ink font-medium underline-offset-4 hover:underline pt-3">
              Read our mission <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TOPIC SNAPSHOT */}
      <section className="section-y">
        <div className="container-x">
          <SectionHeader
            eyebrow="Topics we cover"
            title="A snapshot of what workshops can include."
            description="Every workshop is shaped to the setting and audience. Topics can be combined, adapted or focused as needed."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {TOPICS.map(({ icon: Icon, title }) => (
              <div key={title} className="bg-white border border-stone rounded-2xl p-6">
                <Icon className="w-5 h-5 text-crimson mb-4" />
                <h3 className="text-base font-heading font-semibold text-ink leading-snug">{title}</h3>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/workshops" data-testid="topics-cta" className="btn-primary">
              See all workshop topics <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <InstagramFeed />

      {/* CTA */}
      <section className="bg-crimson text-white">
        <div className="container-x py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <span className="label-eyebrow !text-white/70">— Work with us</span>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-heading font-semibold tracking-tight leading-[1.1] text-balance">
                Ready to bring a workshop to your team, school or organisation?
              </h2>
              <p className="mt-5 text-white/80 max-w-xl">We tailor every workshop to your setting. Get in touch and we'll shape something that works for you.</p>
            </div>
            <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
              <Link to="/contact" data-testid="home-enquire-btn" className="bg-ink text-white px-7 py-3.5 rounded-full font-medium text-sm hover:bg-black hover:-translate-y-0.5 transition-all">
                Enquire About a Workshop
              </Link>
              <Link to="/workshops" data-testid="home-explore-btn" className="border border-white text-white px-7 py-3.5 rounded-full font-medium text-sm hover:bg-white hover:text-crimson transition-all">
                Explore Our Workshops
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
